import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

const quickLinks = [
  { to: "/ranking", label: "人气排行榜" },
  { to: "/category", label: "热门分类" },
  { to: "/about", label: "关于我们" },
  { to: "/faq", label: "常见问题" },
];

export default function Footer() {
  const [status, setStatus] = useState<"idle" | "sending" | "ok" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  async function handleSubscribe(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === "sending" || subscribed) return;
    const form = e.currentTarget;
    const data = new FormData(form);
    const honeypot = (data.get("website_alt") as string) || "";

    // honeypot hit: simulate generic success without sending anything
    if (honeypot.trim() !== "") {
      setSubscribed(true);
      setStatus("ok");
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key !== "website_alt" && value) payload.append(key, value as string);
    });

    setStatus("sending");
    setFormError("");
    try {
      const res = await fetch("https://readdy.ai/api/form/da0ppl6th95mubjtuibg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (
        !res.ok ||
        parsed?.code !== "OK" ||
        String(msg).toLowerCase().includes("spam")
      ) {
        setStatus("error");
        setFormError(msg ? String(msg) : "订阅失败,请稍后再试");
        return;
      }
      setSubscribed(true);
      setStatus("ok");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常,订阅失败,请稍后再试");
    }
  }

  return (
    <footer className="bg-foreground-950 text-background-200">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Link to="/" className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-500 text-background-50">
                <i className="ri-book-3-fill text-lg"></i>
              </span>
              <span className="font-heading text-xl text-background-50">
                漫糖<span className="text-primary-400">韩漫</span>
              </span>
            </Link>
            <p className="mt-4 text-sm leading-relaxed text-background-400">
              免费韩漫高分榜单,实时更新人气推荐。从甜宠到悬疑,帮你快速找到对味的作品,畅享优质韩漫阅读。
            </p>
            <div className="mt-5 flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-foreground-800 text-background-200">
                <i className="ri-wechat-line"></i>
              </span>
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-foreground-800 text-background-200">
                <i className="ri-mail-line"></i>
              </span>
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-foreground-800 text-background-200">
                <i className="ri-twitter-x-line"></i>
              </span>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-bold uppercase tracking-wide text-background-50">热门分类</h4>
            <ul className="mt-4 grid grid-cols-2 gap-2.5">
              {categories.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/category/${c.slug}`}
                    className="text-sm text-background-400 transition-colors hover:text-primary-400"
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-bold uppercase tracking-wide text-background-50">快速导航</h4>
            <ul className="mt-4 space-y-2.5">
              {quickLinks.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-sm text-background-400 transition-colors hover:text-primary-400"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-bold uppercase tracking-wide text-background-50">订阅周刊</h4>
            <p className="mt-4 text-sm leading-relaxed text-background-400">
              每周精选高分韩漫上新与榜单动态,第一时间送到你的邮箱。
            </p>
            {subscribed ? (
              <div className="mt-4 flex items-center gap-2 rounded-xl bg-secondary-500/20 px-4 py-3 text-sm text-secondary-200">
                <i className="ri-checkbox-circle-fill text-secondary-300"></i>
                订阅成功,感谢你的关注!
              </div>
            ) : (
              <form onSubmit={handleSubscribe} data-readdy-form className="mt-4 space-y-3">
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="输入你的邮箱"
                  className="w-full rounded-lg border border-foreground-700 bg-foreground-900 px-3.5 py-2.5 text-sm text-background-50 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
                />
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600 disabled:opacity-60"
                >
                  {status === "sending" ? (
                    <i className="ri-loader-4-line animate-spin"></i>
                  ) : (
                    <i className="ri-send-plane-fill"></i>
                  )}
                  立即订阅
                </button>
                {status === "error" && formError && (
                  <p className="flex items-center gap-1 text-xs text-red-400">
                    <i className="ri-error-warning-line"></i>
                    {formError}
                  </p>
                )}
              </form>
            )}
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-foreground-800 pt-6 sm:flex-row">
          <p className="text-xs text-foreground-500">
            © {new Date().getFullYear()} 漫糖韩漫 · 免费韩漫排行榜与人气推荐
          </p>
          <p className="text-xs text-foreground-500">
            内容仅供推荐参考,版权归原作者所有 · 请支持正版
          </p>
        </div>
      </div>
    </footer>
  );
}